'use client'

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import MeetupForm from '@/features/meetups/meetup-form'
import Alert from '@/components/ui/alert'
import Button from '@/components/ui/button'
import { getMeetupById } from '@/services/meetups'
import { useUser } from '@/hooks/use-user'

function EditMeetupPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useUser()
  const [meetup, setMeetup] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadMeetup() {
      if (!id) return

      try {
        setIsLoading(true)
        const { data, error } = await getMeetupById(id as string)
        
        if (error) throw new Error(error)
        if (!data) throw new Error('Meetup not found')
        
        setMeetup(data)
      } catch (err) {
        console.error('Error loading meetup:', err)
        setError(err instanceof Error ? err.message : 'Failed to load meetup')
      } finally {
        setIsLoading(false)
      }
    }

    if (id) loadMeetup()
  }, [id])

  const handleSuccess = (data: any) => {
    navigate(`/meetups/${data.id}`)
  }

  const handleCancel = () => {
    navigate(`/meetups/${id}`)
  }

  // Check if user is authorized to edit this meetup
  const isAuthorized = user && meetup && user.id === meetup.host_id

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="error" title="Error">
          {error}
        </Alert>
        <div className="mt-4">
          <Button onClick={() => navigate('/meetups')}>
            Back to Meetups
          </Button>
        </div>
      </div>
    )
  }

  if (!isAuthorized) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="warning" title="Permission Denied">
          You do not have permission to edit this meetup.
        </Alert>
        <div className="mt-4">
          <Button onClick={() => navigate(`/meetups/${id}`)}>
            View Meetup Details
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Edit Meetup</h1>
      
      <div className="bg-gray-800 p-6 rounded-lg">
        <MeetupForm 
          initialData={meetup}
          userId={user.id}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </div>
    </div>
  )
}

export default EditMeetupPage
